'use client';

import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { FaChevronRight, FaCheck, FaTimes, FaLock } from 'react-icons/fa';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useBenzingaEdge } from '@benzinga/edge';
import { QuotesManager, Logo, DelayedQuote } from '@benzinga/quotes-manager';
import { CalendarManager, Ratings, Economics } from '@benzinga/calendar-manager';
import { ScannerManager, QuoteProtos } from '@benzinga/scanner-manager';
import { CryptoManager, CoinData } from '@benzinga/crypto-manager';
import { Table, ColumnDef } from '@benzinga/table';
import { BzImage } from '@benzinga/image';
import { TickerLink } from '@benzinga/ticker-ui';
import { useScrollShadow } from '@benzinga/hooks';
import { formatPriceWithCurrency, formatLarge, numberShorthand } from '@benzinga/utils';
import { SCANNER_QUERY } from './constants';
import { IMAGE_REQUEST_PARAMS, STOCK_SYMBOLS, CRYPTO_SYMBOLS } from './constants';
import styles from './CalendarInsightsWidget.module.css';

const EDGE_TRIAL_URL = 'https://www.benzinga.com/premium/ideas/benzinga-edge-7-days-free-trial/';

type TabType = 'rankings' | 'ratings' | 'economics' | 'stocks' | 'crypto';

const TAB_CONFIG: Record<TabType, { label: string; link: string; viewMoreLabel: string }> = {
  rankings: { label: 'Top Value Stocks', link: '/screener/top-value-stocks', viewMoreLabel: 'View the Full List' },
  ratings: { label: 'Analyst Ratings', link: '/analyst-stock-ratings', viewMoreLabel: 'View the Full Calendar' },
  economics: { label: 'Economic Calendar', link: '/calendars/economic', viewMoreLabel: 'View the Full Calendar' },
  stocks: { label: 'Stock Screener', link: '/screener', viewMoreLabel: 'View Full Screener' },
  crypto: { label: 'Crypto Spotlight', link: '/crypto', viewMoreLabel: 'View Crypto Page' },
};

interface RankedStockData extends QuoteProtos.IQuote {
  logo?: string;
}

interface StockDataWithLogo extends DelayedQuote {
  logo?: string;
}

interface RatingsWithLogo extends Ratings {
  logo?: string;
}

type CalendarRowData = RankedStockData | RatingsWithLogo | Economics | StockDataWithLogo | CoinData;
type CalendarColumnDef = ColumnDef<CalendarRowData>;

const getLogoUrlFromLogo = (logo: Logo | undefined): string | undefined => {
  return logo?.files?.mark_light ?? logo?.files?.logo_light;
};

export const TickerCellContent: React.FC<{ logo?: string | null; symbol: string; name?: string | null }> = ({
  logo,
  symbol,
  name,
}) => (
  <div className="flex items-center gap-2.5">
    {logo && (
      <div className="w-8 h-8 min-w-8 min-h-8 rounded-full overflow-hidden flex items-center justify-center bg-slate-100 border border-bzblue-300">
        <BzImage alt={symbol} height={24} width={24} src={logo} loader={({ src }) => src} />
      </div>
    )}
    <div className="flex flex-col">
      <TickerLink symbol={symbol} className="font-bold text-sm text-[#192940] no-underline hover:underline">
        {symbol}
      </TickerLink>
      <TickerLink symbol={symbol}>
        <span className="text-xs text-bzgray-600 max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">
          {name}
        </span>
      </TickerLink>
    </div>
  </div>
);

const LockedTickerCell: React.FC = () => (
  <a
    href={`${EDGE_TRIAL_URL}?adType=calendar_insights_widget&ad=ticker&campaign=ranking`}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 no-underline bg-blue-50/80 -m-3 p-3 w-[calc(100%+24px)]"
    title=""
  >
    <div className="w-7 h-7 min-w-7 min-h-7 rounded flex items-center justify-center bg-white">
      <FaLock className="text-blue-500 text-xs" />
    </div>
    <div className="flex flex-col">
      <span className="font-semibold text-xs sm:text-sm text-slate-700">
        <span className="text-blue-500 font-bold">EDGE</span> Subscribers Only
      </span>
      <span className="text-[11px] text-blue-500 hover:underline">Get Access</span>
    </div>
  </a>
);

const TrendIndicator: React.FC<{ trend?: string | null }> = ({ trend }) => {
  const isPositive = trend === 'Y';

  return (
    <div
      className={`w-12 h-4 rounded-full flex items-center justify-center ${
        isPositive ? 'bg-green-100' : trend ? 'bg-red-100' : 'bg-slate-100'
      }`}
    >
      {isPositive ? (
        <FaCheck className="text-green-500 text-[10px]" />
      ) : trend ? (
        <FaTimes className="text-red-500 text-[10px]" />
      ) : (
        <span className="text-slate-400 text-xs font-medium">—</span>
      )}
    </div>
  );
};

const PriceTrendCell: React.FC<{
  shortTrend?: string | null;
  mediumTrend?: string | null;
  longTrend?: string | null;
}> = ({ shortTrend, mediumTrend, longTrend }) => (
  <div className="flex items-center gap-3">
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] text-bzgray-600 font-medium">Short</span>
      <TrendIndicator trend={shortTrend} />
    </div>
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] text-bzgray-600 font-medium">Medium</span>
      <TrendIndicator trend={mediumTrend} />
    </div>
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] text-bzgray-600 font-medium">Long</span>
      <TrendIndicator trend={longTrend} />
    </div>
  </div>
);

const ScoreCell: React.FC<{ score?: number | null; color: string }> = ({ score, color }) => {
  if (score === undefined || score === null) return <span className="text-gray-400">—</span>;
  return <span className={`font-bold text-base ${color}`}>{score.toFixed(2)}</span>;
};

const getScoreColor = (score?: number | null): string => {
  if (score === undefined || score === null) return 'text-gray-400';
  if (score === 0) return 'text-[#b8b8b8]';
  if (score <= 33) return 'text-red-500';
  if (score <= 66) return 'text-yellow-500';
  return 'text-green-500';
};

const SCREENER_HEADER_LINKS: Record<string, string> = {
  momentumPercentile: '/screener/top-momentum-stocks',
  growthPercentile: '/screener/top-growth-stocks',
  valuePercentile: '/screener/top-value-stocks',
  qualityPercentile: '/screener/top-quality-stocks',
};

const ScreenerHeaderLink: React.FC<{ label: string; href: string }> = ({ label, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-1 no-underline text-inherit hover:opacity-80 capitalize"
  >
    <span>{label}</span>
    <span className="opacity-60 text-[8px] leading-none flex flex-col">
      <span>▲</span>
      <span>▼</span>
    </span>
  </a>
);

const getRankingsColumnsDef = (hasEdgeAccess: boolean): ColumnDef<RankedStockData>[] => [
  {
    field: 'symbol',
    headerName: 'Ticker',
    width: 200,
    cellClass: hasEdgeAccess ? undefined : '!px-[10px]',
    shouldRender: !hasEdgeAccess,
    cellRenderer: ({ data }: { data: RankedStockData }) =>
      hasEdgeAccess ? (
        <TickerCellContent logo={data.logo} symbol={(data.symbol ?? '').toUpperCase()} name={data.name} />
      ) : (
        <LockedTickerCell />
      ),
  },
  {
    field: 'shortTermTrend',
    headerName: 'Price Trend',
    headerClass: 'text-center',
    cellClass: 'flex justify-center',
    cellRenderer: ({ data }: { data: QuoteProtos.IQuote }) => (
      <PriceTrendCell
        shortTrend={data.shortTermTrend}
        mediumTrend={data.mediumTermTrend}
        longTrend={data.longTermTrend}
      />
    ),
  },
  {
    field: 'momentumPercentile',
    headerName: 'Momentum',
    headerRenderer: () => <ScreenerHeaderLink label="Momentum" href={SCREENER_HEADER_LINKS.momentumPercentile} />,
    cellRenderer: ({ data }: { data: QuoteProtos.IQuote }) => (
      <ScoreCell score={data.momentumPercentile} color={getScoreColor(data.momentumPercentile)} />
    ),
  },
  {
    field: 'growthPercentile',
    headerName: 'Growth',
    headerRenderer: () => <ScreenerHeaderLink label="Growth" href={SCREENER_HEADER_LINKS.growthPercentile} />,
    cellRenderer: ({ data }: { data: QuoteProtos.IQuote }) => (
      <ScoreCell score={data.growthPercentile} color={getScoreColor(data.growthPercentile)} />
    ),
  },
  {
    field: 'valuePercentile',
    headerName: 'Value',
    headerRenderer: () => <ScreenerHeaderLink label="Value" href={SCREENER_HEADER_LINKS.valuePercentile} />,
    cellRenderer: ({ data }: { data: QuoteProtos.IQuote }) => (
      <ScoreCell score={data.valuePercentile} color={getScoreColor(data.valuePercentile)} />
    ),
  },
  {
    field: 'qualityPercentile',
    headerName: 'Quality',
    headerRenderer: () => <ScreenerHeaderLink label="Quality" href={SCREENER_HEADER_LINKS.qualityPercentile} />,
    cellRenderer: ({ data }: { data: QuoteProtos.IQuote }) => (
      <ScoreCell score={data.qualityPercentile} color={getScoreColor(data.qualityPercentile)} />
    ),
  },
];

const RatingChangeCell: React.FC<{ prior?: string; current?: string; actionCompany?: string }> = ({
  prior,
  current,
  actionCompany,
}) => {
  const displayPrior = actionCompany === 'Initiates Coverage On' ? 'Initiates' : prior;
  const isUpgrade = actionCompany === 'Upgrades' || actionCompany === 'Initiates Coverage On';
  const isDowngrade = actionCompany === 'Downgrades';

  const currentColorClass = isUpgrade ? 'text-bzgreen-600' : isDowngrade ? 'text-bzred-500' : 'text-bzgray-900';

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold text-bzgray-800 uppercase">{displayPrior || '—'}</span>
      <span className="text-bzgray-600">→</span>
      <span className={`text-sm font-bold uppercase ${currentColorClass}`}>{current || '—'}</span>
    </div>
  );
};

const PriceTargetChangeCell: React.FC<{ prior?: string; current?: string; actionPt?: string }> = ({
  prior,
  current,
  actionPt,
}) => {
  const priorValue = prior ? Number(prior) : null;
  const currentValue = current ? Number(current) : null;
  const isRaised = actionPt === 'Raises';
  const isLowered = actionPt === 'Lowers';

  const currentColorClass = isRaised ? 'text-bzgreen-600' : isLowered ? 'text-bzred-500' : 'text-bzgray-900';

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold text-bzgray-800">
        {priorValue ? formatPriceWithCurrency(priorValue) : '—'}
      </span>
      <span className="text-bzgray-600">→</span>
      <span className={`text-sm font-bold ${currentColorClass}`}>
        {currentValue ? formatPriceWithCurrency(currentValue) : '—'}
      </span>
    </div>
  );
};

const formatRatingsDate = (date?: string): string => {
  if (!date) return '—';
  try {
    return DateTime.fromFormat(date, 'yyyy-LL-dd').toFormat('LLL d, yyyy');
  } catch {
    return date;
  }
};

const RatingsColumnsDef: ColumnDef<Ratings & { logo?: string }>[] = [
  {
    field: 'date',
    headerName: 'Date',
    width: 120,
    valueFormatter: ({ data }: { data: Ratings }) => formatRatingsDate(data.date),
  },
  {
    field: 'ticker',
    headerName: 'Ticker',
    width: 200,
    cellRenderer: ({ data }: { data: Ratings & { logo?: string } }) => (
      <TickerCellContent logo={data.logo} symbol={data.ticker.toUpperCase()} name={data.name} />
    ),
  },
  {
    field: 'current_price',
    headerName: 'Current Price',
    valueFormatter: ({ data }: { data: Ratings & { lastTradePrice?: number } }) => {
      const price = data?.lastTradePrice;
      return price ? formatPriceWithCurrency(price) : '—';
    },
    valueGetter: ({ data }: { data: Ratings & { lastTradePrice?: number } }) =>
      data?.lastTradePrice ? Number(data?.lastTradePrice) : null,
  },
  {
    field: 'analyst',
    headerName: 'Analyst',
    cellRenderer: ({ data }: { data: Ratings }) => (
      <div className="flex flex-col">
        <span className="font-bold text-sm text-bzgray-800">{data.analyst_name || '—'}</span>
        <span className="text-xs text-bzgray-600">{data.analyst || '—'}</span>
      </div>
    ),
  },
  {
    field: 'rating_current',
    headerName: 'Previous / Current Rating',
    cellRenderer: ({ data }: { data: Ratings }) => (
      <RatingChangeCell prior={data.rating_prior} current={data.rating_current} actionCompany={data.action_company} />
    ),
  },
  {
    field: 'pt_current',
    headerName: 'Price Target Change',
    cellRenderer: ({ data }: { data: Ratings }) => (
      <PriceTargetChangeCell prior={data.pt_prior} current={data.pt_current} actionPt={data.action_pt} />
    ),
  },
  {
    field: 'action_company',
    headerName: 'Action',
    cellRenderer: ({ data }: { data: Ratings }) => {
      const action = data.action_company;
      const isUpgrade = action === 'Upgrades';
      const isDowngrade = action === 'Downgrades';
      return (
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${isUpgrade ? 'bg-bzgreen-100 text-bzgreen-600' : isDowngrade ? 'bg-bzred-100 text-bzred-500' : 'bg-bzgray-100 text-bzgray-600'}`}
        >
          {action || '—'}
        </span>
      );
    },
  },
];

const formatTime = (time?: string): string => {
  if (!time) return '—';
  try {
    return DateTime.fromFormat(time, 'HH:mm:ss').toFormat('h:mm a');
  } catch {
    return time;
  }
};

const formatEconomicsDate = (date?: string): string => {
  if (!date) return '—';
  try {
    return DateTime.fromISO(date).toFormat('MM/dd/yyyy');
  } catch {
    return date;
  }
};

const formatEconomicsValue = (value: string | number | null | undefined): string => {
  if (value === '' || value === null || value === undefined) return '—';
  return numberShorthand(value, 2, true) || '—';
};

const COUNTRY_FLAG_BASE =
  'https://www.benzinga.com/wp-content/themes/benzinga-money/dist/rebuild/images/icons/countries-flags/alpha3';

const EconomicsColumnsDef: ColumnDef<Economics>[] = [
  {
    field: 'date',
    headerName: 'Date',
    width: 100,
    valueFormatter: ({ data }: { data: Economics }) => formatEconomicsDate(data.date),
  },
  {
    field: 'time',
    headerName: 'Time',
    width: 110,
    valueFormatter: ({ data }: { data: Economics }) => formatTime(data.time),
  },
  {
    field: 'country',
    headerName: 'Country',
    width: 90,
    cellRenderer: ({ data }: { data: Economics }) => {
      const country = data.country;
      if (!country) return <span className="text-gray-400">—</span>;
      const flagSrc = `${COUNTRY_FLAG_BASE}/${country.toUpperCase()}.svg`;
      return (
        <div className="flex items-center gap-1.5">
          <div className="border border-gray-300 rounded-[2px] overflow-hidden w-[17px] h-[12px] flex-shrink-0 relative">
            <BzImage
              alt={country}
              layout="fill"
              objectFit="cover"
              src={flagSrc}
              loader={({ src }) => src}
              loading="lazy"
            />
          </div>
          <span>{country}</span>
        </div>
      );
    },
  },
  {
    field: 'event_name',
    headerName: 'Econ Event',
    width: 200,
    valueFormatter: ({ data }: { data: Economics }) => data.event_name || '—',
  },
  {
    field: 'event_period',
    headerName: 'Period',
    width: 80,
    valueFormatter: ({ data }: { data: Economics }) => {
      if (!data.event_period && !data.period_year) return '—';
      return `${data.event_period || ''} ${data.period_year || ''}`.trim();
    },
  },
  {
    field: 'prior',
    headerName: 'Prior',
    width: 100,
    valueFormatter: ({ data }: { data: Economics }) => formatEconomicsValue(data.prior),
  },
  {
    field: 'consensus',
    headerName: 'Forecast',
    width: 100,
    valueFormatter: ({ data }: { data: Economics }) => formatEconomicsValue(data.consensus),
  },
  {
    field: 'actual',
    headerName: 'Actual',
    width: 100,
    cellRenderer: ({ data }: { data: Economics }) => {
      const actual = data.actual;
      const consensus = data.consensus;
      if (actual === '' || actual === null || actual === undefined) return <span className="text-gray-400">—</span>;
      const actualNum = Number(actual);
      const consensusNum = Number(consensus);
      const isBetter = actualNum > consensusNum;
      const isWorse = actualNum < consensusNum;
      return (
        <span className={`font-extrabold ${isBetter ? 'text-green-600' : isWorse ? 'text-red-600' : 'text-[#283d59]'}`}>
          {formatEconomicsValue(actual)}
        </span>
      );
    },
  },
];

const ChangeCellContent: React.FC<{ changeValue?: number; changePercent?: number; isCrypto?: boolean }> = ({
  changeValue,
  changePercent,
  isCrypto,
}) => {
  const isPositive = (changePercent ?? 0) >= 0;
  return (
    <div className="flex items-center gap-1.5">
      <span className={`font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {changeValue !== undefined
          ? (changeValue >= 0 ? '+' : '') + `$${isCrypto ? Math.abs(changeValue).toFixed(2) : changeValue.toFixed(2)}`
          : '—'}
      </span>
      <span
        className={`px-1.5 py-0.5 rounded text-xs font-medium ${isPositive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
      >
        {changePercent !== undefined ? `${changePercent.toFixed(2)}%` : '—'}
      </span>
    </div>
  );
};

const StockColumnsDef: ColumnDef<StockDataWithLogo>[] = [
  {
    field: 'symbol',
    headerName: 'Ticker',
    cellRenderer: ({ data }: { data: StockDataWithLogo }) => (
      <TickerCellContent logo={data.logo} symbol={data.symbol?.toUpperCase()} name={data.name} />
    ),
  },
  {
    field: 'lastTradePrice',
    headerName: 'Price',
    valueFormatter: ({ data }: { data: DelayedQuote }) =>
      data.lastTradePrice ? `$${data.lastTradePrice.toFixed(2)}` : '—',
    width: 150,
  },
  {
    field: 'change',
    headerName: 'Change',
    cellRenderer: ({ data }: { data: DelayedQuote }) => (
      <ChangeCellContent changeValue={data.change} changePercent={data.changePercent} />
    ),
  },
  {
    field: 'volume',
    headerName: 'Volume',
    valueFormatter: ({ data }: { data: DelayedQuote }) => (data.volume ? formatLarge(data.volume, 1) : '—'),
  },
  {
    field: 'marketCap',
    headerName: 'Market Cap',
    valueFormatter: ({ data }: { data: DelayedQuote }) => (data.marketCap ? formatLarge(data.marketCap, 2) : '—'),
  },
  {
    field: 'pe',
    headerName: 'P/E',
    valueFormatter: ({ data }: { data: DelayedQuote }) => (data.pe ? data.pe.toFixed(1) : '—'),
  },
  {
    field: 'fiftyTwoWeekHigh',
    headerName: '52W High',
    valueFormatter: ({ data }: { data: DelayedQuote }) =>
      data.fiftyTwoWeekHigh ? `$${data.fiftyTwoWeekHigh.toFixed(2)}` : '—',
  },
  {
    field: 'dividendYield',
    headerName: 'Dividend',
    valueFormatter: ({ data }: { data: DelayedQuote }) =>
      data.dividendYield ? `${data.dividendYield.toFixed(2)}%` : '—',
  },
];

const CryptoColumnsDef: ColumnDef<CoinData>[] = [
  {
    field: 'name',
    headerName: 'Coin',
    cellRenderer: ({ data }: { data: CoinData }) => (
      <TickerCellContent logo={data.image} symbol={(data.symbol ?? '').toUpperCase()} name={data.name} />
    ),
  },
  {
    field: 'current_price',
    headerName: 'Price',
    valueFormatter: ({ data }: { data: CoinData }) =>
      data.current_price
        ? `$${data.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '—',
  },
  {
    field: 'price_change_24h',
    headerName: 'Change',
    cellRenderer: ({ data }: { data: CoinData }) => (
      <ChangeCellContent
        changeValue={data.price_change_24h}
        changePercent={data.price_change_percentage_24h}
        isCrypto
      />
    ),
  },
  {
    field: 'total_volume',
    headerName: 'Volume',
    valueFormatter: ({ data }: { data: CoinData }) => (data.total_volume ? formatLarge(data.total_volume, 1) : '—'),
  },
  {
    field: 'market_cap',
    headerName: 'Market Cap',
    valueFormatter: ({ data }: { data: CoinData }) => (data.market_cap ? formatLarge(Number(data.market_cap), 2) : '—'),
  },
];

export const CalendarInsightsWidget: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('stocks');
  const [rankingsData, setRankingsData] = useState<RankedStockData[]>([]);
  const [ratingsData, setRatingsData] = useState<(Ratings & { logo?: string })[]>([]);
  const [economicsData, setEconomicsData] = useState<Economics[]>([]);
  const [stockData, setStockData] = useState<StockDataWithLogo[]>([]);
  const [cryptoData, setCryptoData] = useState<CoinData[]>([]);
  const [loading, setLoading] = useState(true);

  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const { showStart, showEnd } = useScrollShadow(tabsScrollRef);

  const { hasEdgeSubscription: hasEdgeAccess } = useBenzingaEdge();

  const session = useContext(SessionContext);
  const trackingManager = session.getManager(TrackingManager);
  const quotesManager = session.getManager(QuotesManager);

  const handleSetActiveTab = useCallback(
    (tab: TabType) => {
      setActiveTab(tab);
      const { label } = TAB_CONFIG[tab];
      trackingManager.trackLinkEvent('click', {
        button_type: 'toggle',
        button_id: 'calendar_insights_widget',
        button_label: label,
        button_location: 'homepage',
      });
    },
    [trackingManager],
  );
  const calendarManager = session.getManager(CalendarManager);
  const cryptoManager = session.getManager(CryptoManager);
  const scannerManager = session.getManager(ScannerManager);

  const fetchRankingsData = useCallback(async () => {
    setLoading(true);
    try {
      const scannerRes = await scannerManager.getInstrumentsWithIQuery(SCANNER_QUERY);

      if (scannerRes.ok?.instruments) {
        const instruments = scannerRes.ok.instruments;
        const symbols = instruments.map(inst => inst.symbol).filter(Boolean) as string[];

        const logosMapRes = await quotesManager.getQuotesLogosMapped(symbols, IMAGE_REQUEST_PARAMS);
        const logos = logosMapRes.ok ?? {};

        const dataWithLogos: RankedStockData[] = instruments.map(inst => ({
          ...inst,
          logo: getLogoUrlFromLogo(logos[inst.symbol ?? '']),
        }));
        setRankingsData(dataWithLogos);
      }
    } catch (error) {
      console.error('Error fetching rankings data:', error);
    }
    setLoading(false);
  }, [scannerManager, quotesManager]);

  const fetchRatingsData = useCallback(async () => {
    setLoading(true);
    try {
      const ratingsRes = await calendarManager.getCalendarData(
        'ratings',
        {
          dateFrom: DateTime.now().minus({ days: 5 }).toFormat('yyyy-LL-dd'),
          dateTo: DateTime.now().toFormat('yyyy-LL-dd'),
          pageSize: 40,
        },
        true,
      );

      if (ratingsRes.ok) {
        const ratings = ratingsRes.ok;
        const symbols = ratings.map(rating => rating.ticker).filter(Boolean);

        const [logosMapRes, quotesRes] = await Promise.all([
          quotesManager.getQuotesLogosMapped(symbols, IMAGE_REQUEST_PARAMS),
          quotesManager.getDelayedQuotes(symbols),
        ]);

        const logos = logosMapRes.ok ?? {};
        const quotes = quotesRes.ok ?? {};

        const dataWithLogosAndQuotes = ratings.map(rating => ({
          ...rating,
          logo: getLogoUrlFromLogo(logos[rating.ticker]),
          lastTradePrice: quotes[rating.ticker]?.lastTradePrice ?? rating.quote?.lastTradePrice,
        }));
        setRatingsData(dataWithLogosAndQuotes);
      }
    } catch (error) {
      console.error('Error fetching ratings data:', error);
    }
    setLoading(false);
  }, [calendarManager, quotesManager]);

  const fetchEconomicsData = useCallback(async () => {
    setLoading(true);
    try {
      const economicsRes = await calendarManager.getCalendarData(
        'economics',
        {
          dateFrom: DateTime.now().minus({ days: 5 }).toFormat('yyyy-LL-dd'),
          dateTo: DateTime.now().toFormat('yyyy-LL-dd'),
          pageSize: 20,
        },
        true,
      );

      if (economicsRes.ok) {
        const sortedData = economicsRes.ok.sort((eventA, eventB) => {
          const dateTimeA = DateTime.fromISO(`${eventA.date}T${eventA.time || '00:00:00'}`);
          const dateTimeB = DateTime.fromISO(`${eventB.date}T${eventB.time || '00:00:00'}`);
          return dateTimeB.toMillis() - dateTimeA.toMillis();
        });
        setEconomicsData(sortedData);
      }
    } catch (error) {
      console.error('Error fetching economics data:', error);
    }
    setLoading(false);
  }, [calendarManager]);

  const fetchStockData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesRes, logosRes] = await Promise.all([
        quotesManager.getDelayedQuotes(STOCK_SYMBOLS),
        quotesManager.getQuotesLogosMapped(STOCK_SYMBOLS, IMAGE_REQUEST_PARAMS),
      ]);

      if (quotesRes.ok) {
        const logos = logosRes.ok ?? {};
        const quotesWithLogos: StockDataWithLogo[] = Object.values(quotesRes.ok).map(quote => ({
          ...quote,
          logo: getLogoUrlFromLogo(logos[quote.symbol]),
        }));
        setStockData(quotesWithLogos);
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
    }
    setLoading(false);
  }, [quotesManager]);

  const fetchCryptoData = useCallback(async () => {
    setLoading(true);
    try {
      const cryptoRes = await cryptoManager.getCoins(CRYPTO_SYMBOLS);
      if (cryptoRes.ok) {
        const sortedByMarketCap = [...cryptoRes.ok].sort(
          (coinA, coinB) => Number(coinB.market_cap) - Number(coinA.market_cap),
        );
        setCryptoData(sortedByMarketCap);
      }
    } catch (error) {
      console.error('Error fetching crypto data:', error);
    }
    setLoading(false);
  }, [cryptoManager]);

  useEffect(() => {
    if (activeTab === 'rankings' && rankingsData.length === 0) {
      fetchRankingsData();
    } else if (activeTab === 'ratings' && ratingsData.length === 0) {
      fetchRatingsData();
    } else if (activeTab === 'economics' && economicsData.length === 0) {
      fetchEconomicsData();
    } else if (activeTab === 'stocks' && stockData.length === 0) {
      fetchStockData();
    } else if (activeTab === 'crypto' && cryptoData.length === 0) {
      fetchCryptoData();
    }
  }, [
    activeTab,
    rankingsData.length,
    ratingsData.length,
    economicsData.length,
    stockData.length,
    cryptoData.length,
    fetchRankingsData,
    fetchRatingsData,
    fetchEconomicsData,
    fetchStockData,
    fetchCryptoData,
  ]);

  const getRowData = (): CalendarRowData[] => {
    switch (activeTab) {
      case 'rankings':
        return rankingsData;
      case 'ratings':
        return ratingsData;
      case 'economics':
        return economicsData;
      case 'stocks':
        return stockData;
      case 'crypto':
        return cryptoData;
      default:
        return [];
    }
  };

  const getColumnsDef = (): CalendarColumnDef[] => {
    switch (activeTab) {
      case 'rankings':
        return getRankingsColumnsDef(hasEdgeAccess) as CalendarColumnDef[];
      case 'ratings':
        return RatingsColumnsDef as CalendarColumnDef[];
      case 'economics':
        return EconomicsColumnsDef as CalendarColumnDef[];
      case 'stocks':
        return StockColumnsDef as CalendarColumnDef[];
      case 'crypto':
        return CryptoColumnsDef as CalendarColumnDef[];
      default:
        return [];
    }
  };

  const activeTabConfig = TAB_CONFIG[activeTab];
  const isRankingsLocked = activeTab === 'rankings' && !hasEdgeAccess;

  const viewMoreLink = isRankingsLocked
    ? `${EDGE_TRIAL_URL}?adType=calendar_insights_widget&ad=view-full-list&campaign=ranking`
    : activeTabConfig.link;

  const viewMoreLabel = isRankingsLocked ? 'Unlock Full List with Edge' : activeTabConfig.viewMoreLabel;

  const tabButtonClass = (isActive: boolean) =>
    `px-4 py-2 rounded border-none text-sm font-semibold cursor-pointer transition-all ${
      isActive ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-50 text-slate-500 hover:bg-slate-100'
    }`;

  return (
    <div className="bg-white border border-bzblue-300 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-bzblue-300 flex-wrap gap-3">
        <div className="relative flex-1 overflow-hidden">
          <div
            ref={tabsScrollRef}
            className={classNames(`overflow-x-auto no-scrollbar`, {
              [styles.scrollableTabsWrapper]: true,
            })}
          >
            <div className="flex gap-2 whitespace-nowrap">
              <button className={tabButtonClass(activeTab === 'stocks')} onClick={() => handleSetActiveTab('stocks')}>
                Stock Screener
              </button>
              <button
                className={tabButtonClass(activeTab === 'rankings')}
                onClick={() => handleSetActiveTab('rankings')}
              >
                Top Value Stocks
              </button>
              <button className={tabButtonClass(activeTab === 'ratings')} onClick={() => handleSetActiveTab('ratings')}>
                Analyst Ratings
              </button>
              <button
                className={tabButtonClass(activeTab === 'economics')}
                onClick={() => handleSetActiveTab('economics')}
              >
                Economic Calendar
              </button>
              <button className={tabButtonClass(activeTab === 'crypto')} onClick={() => handleSetActiveTab('crypto')}>
                Crypto Spotlight
              </button>
            </div>
          </div>
          {showStart && (
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent" />
          )}
          {showEnd && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent" />
          )}
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <Table
          columnsDef={getColumnsDef()}
          rowData={getRowData()}
          height={400}
          mobileHeight={300}
          isLoading={loading}
          noDataText="No data available"
        />
      </div>
      <div className="flex justify-center p-2 bg-blue-50 border-t border-bzgray-200">
        <a
          href={viewMoreLink}
          target="_blank"
          rel="noopener noreferrer"
          data-action={`Calendar Insights Widget View More Click: ${activeTabConfig.label}`}
          className="flex items-center gap-1 text-blue-500 text-sm font-bold no-underline hover:underline"
        >
          <span>
            {isRankingsLocked && <FaLock className="text-[10px] inline mr-1" />}
            {viewMoreLabel}
          </span>
          {!isRankingsLocked && <FaChevronRight className="text-[10px]" />}
        </a>
      </div>
    </div>
  );
};

export default CalendarInsightsWidget;
